<template>
  <div class="page-view">

    <BasePageHeader :title="isNew ? $t('projectView.newProject') : (form.name || $t('projectView.project'))" @back="$router.back()">
      <template #actions>
        <router-link
          v-if="isWarehouseGlance"
          class="btn ghost small"
          :to="{ path: route.path, query: { ...route.query, edit: 'full' } }"
        >{{ $t('ux.glanceEditFull') }}</router-link>
        <router-link
          v-else-if="!isNew && shell.isWarehouse"
          class="btn ghost small"
          :to="{ path: route.path, query: { ...route.query, edit: undefined } }"
        >{{ $t('ux.glanceOps') }}</router-link>
        <RunMacroButton v-if="!isNew && showFullEditor" model-type="project" :model-id="projectId" />
        <button v-if="showFullEditor || isNew" class="btn primary sm" :disabled="saving || !formValid" @click="save">
          {{ saving ? $t('common.saving') : (isNew ? $t('common.create') : $t('common.save')) }}
        </button>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <template v-else>

      <!-- ── Actions (existing projects only) ── -->
      <div v-if="!isNew" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <ProjectActions
          :status-actions="statusActions"
          :advancing-status="advancingStatus"
          :checking-in-all="checkingInAll"
          :project-id="projectId"
          @advance-status="advanceStatus"
          @check-in-all="checkInAll"
        />
      </div>

      <!-- Form -->
      <div class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('projectView.projectDetails') }}</div>

        <!-- Customer live-search -->
        <BaseFormField :label="$t('projectView.customerLabel')">
          <div class="flex items-center justify-start mb-2">
            <button 
              type="button"
              @click="toggleNewCustomerForm" 
              class="ml-6"
              style="background-color: #2563eb; color: white; padding: 0.25rem 0.75rem; border-radius: 0.375rem; border: 1px solid #3b82f6; cursor: pointer; font-size: 0.75rem; font-weight: 600;">
              {{ showNewCustomerForm ? $t('projectView.cancelNewCustomer') : $t('projectView.newCustomer') }}
            </button>
          </div>
          <div class="relative" ref="customerPickerRef">
            <div class="relative">
              <BaseInput
                v-model="customerQuery"
                :placeholder="$t('projectView.searchCustomers')"
                autocomplete="off"
                @input="onCustomerInput"
                @focus="customerDropOpen = true"
                @blur="onCustomerBlur"
              />
              <button
                v-if="selectedCustomer"
                class="absolute right-2.5 top-1/2 -translate-y-1/2 bg-transparent border-none text-gray-500 cursor-pointer flex p-0.5 hover:text-gray-100"
                @click="clearCustomer"
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-[15px] h-[15px]">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div
              v-if="customerDropOpen && customerResults.length"
              class="absolute top-[calc(100%+4px)] left-0 right-0 bg-gray-800 border border-gray-600 rounded-[.625rem] z-50 max-h-48 overflow-y-auto shadow-[0_8px_24px_rgba(0,0,0,.6)]"
            >
              <button
                v-for="c in customerResults"
                :key="c.id"
                class="flex flex-col w-full text-left bg-transparent border-none px-3.5 py-2.5 cursor-pointer border-b border-gray-600 last:border-b-0 hover:bg-gray-600 transition-colors"
                @mousedown.prevent="selectCustomer(c)"
              >
                <span class="text-[.9rem] text-gray-100 font-medium">{{ c.name }}</span>
                <span v-if="c.phone" class="text-[.75rem] text-gray-400 mt-0.5">{{ c.phone }}</span>
              </button>
            </div>
            <div
              v-else-if="customerDropOpen && customerSearching"
              class="absolute top-[calc(100%+4px)] left-0 right-0 bg-gray-800 border border-gray-600 rounded-[.625rem] z-50 px-4 py-3 text-sm text-gray-500 text-center"
            >
              {{ $t('projectView.searchingCustomers') }}
            </div>
            <div
              v-else-if="customerDropOpen && customerQuery.length >= 2 && !customerSearching && !customerResults.length"
              class="absolute top-[calc(100%+4px)] left-0 right-0 bg-gray-800 border border-gray-600 rounded-[.625rem] z-50 px-4 py-3 text-sm text-gray-500 text-center"
            >
              {{ $t('projectView.noCustomersFound') }}
            </div>
          </div>
          
          <div v-if="showNewCustomerForm" style="margin-top: 0.75rem; padding: 1rem; background: var(--color-surface); border: 1px solid #1e3a5f; border-radius: 0.75rem;">
            <div style="font-size: 0.85rem; font-weight: 700; color: #60a5fa; margin-bottom: 0.75rem;">{{ $t('projectView.newCustomerHeader') }}</div>
            <BaseFormField :label="$t('common.name')" required>
              <BaseInput 
                v-model="newCustomerForm.name" 
                :placeholder="$t('projectView.newCustomerNamePlaceholder')"
              />
            </BaseFormField>
            <BaseFormField :label="$t('checkout.email')">
              <BaseInput 
                v-model="newCustomerForm.email" 
                :placeholder="$t('checkout.email')" 
                type="email"
              />
            </BaseFormField>
            <BaseFormField :label="$t('checkout.phone')">
              <BaseInput 
                v-model="newCustomerForm.phone" 
                :placeholder="$t('checkout.phone')"
              />
            </BaseFormField>
            <BaseFormField :label="$t('customers.address')">
              <BaseInput 
                v-model="newCustomerForm.address" 
                :placeholder="$t('customers.addressPlaceholder')"
              />
            </BaseFormField>
            <BaseFormField :label="$t('customers.contactPerson')">
              <BaseInput 
                v-model="newCustomerForm.contact_person" 
                :placeholder="$t('customers.contactPersonPlaceholder')"
              />
            </BaseFormField>
            <div style="display: flex; gap: 0.5rem; justify-content: flex-end;">
              <button 
                type="button"
                @click="toggleNewCustomerForm" 
                style="background: none; border: 1px solid #374151; color: #9ca3af; border-radius: 0.5rem; padding: 0.45rem 0.875rem; font-size: 0.8rem; cursor: pointer;">
                {{ $t('common.cancel') }}
              </button>
              <button 
                type="button"
                @click="saveNewCustomer" 
                :disabled="savingCustomer || !newCustomerForm.name"
                style="background: #2563eb; color: #fff; border: none; border-radius: 0.5rem; padding: 0.45rem 0.875rem; font-size: 0.8rem; font-weight: 600; cursor: pointer;"
                :style="{ opacity: (savingCustomer || !newCustomerForm.name) ? '0.4' : '1', cursor: (savingCustomer || !newCustomerForm.name) ? 'not-allowed' : 'pointer' }">
                {{ savingCustomer ? $t('common.saving') : $t('common.create') }}
              </button>
            </div>
          </div>
        </BaseFormField>

        <BaseFormField :label="$t('common.name')" required>
          <BaseInput v-model="form.name" :placeholder="$t('projectView.projectNamePlaceholder')" maxlength="200" />
        </BaseFormField>

        <BaseFormField :label="$t('common.location')" required>
          <BaseInput v-model="form.location" :placeholder="$t('projectView.locationPlaceholder')" maxlength="200" />
        </BaseFormField>

        <BaseFormField :label="$t('common.description')">
          <BaseTextarea v-model="form.description" :placeholder="$t('projectView.descriptionPlaceholder')" :rows="3" />
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('projectView.startLabel')" required>
            <BaseInput type="datetime-local" v-model="form.start_time_local" />
          </BaseFormField>
          <BaseFormField :label="$t('projectView.endLabel')" required>
            <BaseInput type="datetime-local" v-model="form.end_time_local" />
          </BaseFormField>
        </div>

        <BaseFormField :label="$t('projectView.statusLabel')">
          <BaseSelect v-model.number="form.status">
            <option :value="0">{{ $t('projectView.statusDraft') }}</option>
            <option :value="1">{{ $t('projectView.statusConfirmed') }}</option>
            <option :value="2">{{ $t('projectView.statusReady') }}</option>
            <option :value="3">{{ $t('projectView.statusPickedUp') }}</option>
            <option :value="4">{{ $t('projectView.statusDelivered') }}</option>
            <option :value="5">{{ $t('common.active') }}</option>
            <option :value="6">{{ $t('projectView.statusReturning') }}</option>
            <option :value="7">{{ $t('projectView.statusCompleted') }}</option>
            <option :value="8">{{ $t('projectView.statusCancelled') }}</option>
          </BaseSelect>
        </BaseFormField>

        <BaseFormField :label="$t('projectView.usageDays')" class="max-w-3xs">
          <div class="flex items-center gap-2">
            <BaseInput
              class="w-20 text-center shrink-0"
              type="number"
              min="1"
              step="1"
              v-model="form.usage_days"
              :placeholder="calculatedUsageDays !== null ? String(calculatedUsageDays) : '—'"
            />
            <span class="text-sm text-gray-400 shrink-0">{{ $t('projectView.days') }}</span>
            <span
              v-if="form.usage_days !== '' && form.usage_days !== null"
              class="text-[.65rem] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded shrink-0 bg-[#3d2c10] text-amber-500"
            >{{ $t('projectView.overrideBadge') }}</span>
            <span
              v-else-if="calculatedUsageDays !== null"
              class="text-[.65rem] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded shrink-0 bg-[#1a3050] text-blue-400"
            >{{ $t('projectView.autoBadge') }}</span>
            <button
              v-if="form.usage_days !== '' && form.usage_days !== null"
              class="bg-transparent border-none text-gray-500 cursor-pointer p-0.5 flex items-center hover:text-gray-100"
              type="button"
              :title="$t('projectView.clearOverride')"
              @click="form.usage_days = ''"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-3.5 h-3.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div v-if="form.usage_days !== '' && form.usage_days !== null && calculatedUsageDays !== null" class="form-hint">
            {{ $t('projectView.calculatedDays', { n: calculatedUsageDays }) }}
          </div>
        </BaseFormField>
      </div>

      <!-- ── Pricing & Billing (existing projects only) ── -->
      <div v-if="!isNew && showFullEditor" class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('projectView.pricingBilling') }}</div>

        <!-- Discount field -->
        <BaseFormField :label="$t('projectView.discount')" class="mb-4">
          <div class="flex items-center gap-2">
            <BaseInput
              class="w-20 text-center shrink-0"
              type="number"
              min="0"
              max="100"
              step="1"
              v-model="form.discount_percentage"
              placeholder="0"
            />
            <span class="text-sm text-gray-400 shrink-0">%</span>
          </div>
        </BaseFormField>

        <!-- Price breakdown -->
        <div v-if="projectStats.total_rental_price !== null" class="bg-[#0d1117] border border-gray-800 rounded-[.625rem] px-3.5 py-2.5 flex flex-col gap-1.5 mb-4">
          <div class="flex justify-between items-center text-sm text-gray-400">
            <span class="text-gray-500">{{ $t('projectView.estRentalTotal') }}</span>
            <span class="tabular-nums text-gray-300">{{ formatPrice(projectStats.total_rental_price) }}</span>
          </div>
          <template v-if="form.discount_percentage && parseFloat(form.discount_percentage) > 0">
            <div class="flex justify-between items-center text-sm text-amber-500">
              <span>{{ $t('projectView.discountPct', { pct: form.discount_percentage }) }}</span>
              <span class="tabular-nums">− {{ formatPrice(projectStats.total_rental_price - projectStats.total_rental_price_after_discount) }}</span>
            </div>
            <div class="flex justify-between items-center text-sm border-t border-gray-800 pt-1.5 mt-0.5">
              <span class="text-gray-200 font-semibold text-[.9rem]">{{ $t('projectView.afterDiscount') }}</span>
              <span class="tabular-nums text-gray-100 font-bold text-base">{{ formatPrice(projectStats.total_rental_price_after_discount) }}</span>
            </div>
          </template>
          <div v-else class="flex justify-between items-center text-sm border-t border-gray-800 pt-1.5 mt-0.5">
            <span class="text-gray-200 font-semibold text-[.9rem]">{{ $t('projectView.totalLabel') }}</span>
            <span class="tabular-nums text-gray-100 font-bold text-base">{{ formatPrice(projectStats.total_rental_price) }}</span>
          </div>
        </div>
        <div v-else class="text-sm text-gray-600 mb-4">{{ $t('projectView.noItemsPricingHint') }}</div>

        <!-- Existing invoice -->
        <template v-if="invoice">
          <div class="bg-[#0d1117] border border-gray-800 rounded-[.625rem] p-3.5 mt-2">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="font-semibold text-gray-100 text-[.9rem]">{{ $t('ui.invoice') }}{{ invoice.id }}</span>
              <span class="text-[.65rem] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">{{ invoice.status }}</span>
            </div>
            <div class="flex justify-between text-[.8rem] text-gray-500 mb-3">
              <span>{{ invoice.invoice_date }}</span>
              <span class="tabular-nums text-gray-300">{{ formatPrice(invoice.total_amount) }}</span>
            </div>
            <div class="flex gap-2">
              <button
                v-if="invoice.status === 'not_invoiced'"
                class="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 border border-green-800 rounded-lg text-green-400 text-[.8rem] cursor-pointer hover:bg-[#14532d] disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
                @click="onMarkInvoiced"
                :disabled="markingInvoiced"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-3.5 h-3.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {{ markingInvoiced ? $t('common.saving') : $t('projectView.markAsInvoiced') }}
              </button>
              <button class="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-gray-300 text-[.8rem] cursor-pointer hover:bg-gray-700 disabled:opacity-45 disabled:cursor-not-allowed transition-colors" @click="exportInvoice" :disabled="exportingInvoice">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {{ exportingInvoice ? $t('projectView.exporting') : $t('projectView.export') }}
              </button>
              <button
                v-if="invoice.can_regenerate === 'true' || invoice.can_regenerate === true"
                class="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 border border-amber-900 rounded-lg text-amber-500 text-[.8rem] cursor-pointer hover:bg-[#3d2c10] disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
                @click="onGenerateInvoice"
                :disabled="generatingInvoice"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
                  <polyline points="1 4 1 10 7 10"/>
                  <path d="M3.51 15a9 9 0 1 0 .49-4"/>
                </svg>
                {{ generatingInvoice ? $t('projectView.regenerating') : $t('projectView.regenerate') }}
              </button>
            </div>
          </div>
        </template>

        <!-- No invoice yet -->
        <template v-else>
          <div v-if="form.status === 7" class="flex flex-col gap-2">
            <template v-if="selectedCustomer">
              <button
                class="flex items-center justify-center gap-2 px-4 py-3 bg-[#1a2e1a] border border-green-800 rounded-[.625rem] text-green-400 text-[.9rem] font-semibold cursor-pointer hover:bg-[#14532d] disabled:opacity-45 disabled:cursor-not-allowed transition-colors w-full"
                @click="onGenerateInvoice"
                :disabled="generatingInvoice || projectStats.total_rental_price === null"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px]">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                {{ generatingInvoice ? $t('projectView.generating') : $t('projectView.generateInvoice') }}
              </button>
              <div v-if="invoiceError" class="form-hint" style="color: var(--color-danger, #f87171);">{{ invoiceError }}</div>
              <div v-else-if="projectStats.total_rental_price === null" class="form-hint">{{ $t('projectView.addItemsBeforeInvoice') }}</div>
            </template>
            <div v-else class="form-hint">{{ $t('projectView.customerRequiredForInvoice') }}</div>
          </div>
          <div v-else class="form-hint">{{ $t('projectView.mustCompleteFirst') }}</div>
        </template>
      </div>

      <!-- ── Documents (existing projects only) ── -->
      <div v-if="!isNew" class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('nav.documents') }}</div>

        <!-- Generate buttons -->
        <div class="flex flex-wrap gap-2 mb-4">
          <button
            class="btn small"
            :disabled="generatingPdf === 'offer'"
            @click="onGeneratePdf('offer')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 mr-1 inline">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            {{ generatingPdf === 'offer' ? $t('projectView.generating') : $t('projectView.generateOffer') }}
          </button>
          <button
            class="btn small"
            :disabled="generatingPdf === 'contract'"
            @click="onGeneratePdf('contract')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 mr-1 inline">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
            </svg>
            {{ generatingPdf === 'contract' ? $t('projectView.generating') : $t('projectView.generateContract') }}
          </button>
          <button
            class="btn small"
            :disabled="generatingPdf === 'picking-list'"
            @click="onGeneratePdf('picking-list')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 mr-1 inline">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            {{ generatingPdf === 'picking-list' ? $t('projectView.generating') : $t('projectView.generatePickingList') }}
          </button>
        </div>

        <!-- Document list -->
        <EmptyState v-if="loadingDocuments" :message="$t('projectView.loadingDocuments')" />
        <div v-else-if="projectDocuments.length" class="list-stack mb-3">
          <div
            v-for="doc in projectDocuments"
            :key="doc.id"
          >
            <div class="data-card">
            <div class="data-card__main">
              <div class="data-card__name">{{ doc.title || doc.document_type_display }}</div>
              <div class="data-card__meta">
                <span class="badge badge-muted">{{ doc.document_type_display }}</span>
                <span v-if="doc.is_signed" class="badge badge-success">{{ $t('documents.signed') }}</span>
                <span class="text-[.75rem] text-[var(--color-muted)]">{{ formatDocDate(doc.created_at) }}</span>
                <span v-if="doc.created_by_email" class="text-[.75rem] text-[var(--color-muted)]">{{ doc.created_by_email }}</span>
              </div>
            </div>
            <div class="data-card__actions flex-wrap gap-1.5 justify-end">
              <!-- Contract-only actions -->
              <template v-if="doc.document_type === 'contract'">
                <button
                  class="btn ghost small flex items-center gap-1"
                  :title="$t('ui.sendSigningRequestByEmail')"
                  @click="toggleSignForm(doc.id)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 shrink-0">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="M2 7l10 6 10-6"/>
                  </svg>
                  <span class="hidden sm:inline">{{ $t('projectView.sendForSigning') }}</span>
                </button>
                <button
                  class="btn ghost small flex items-center gap-1"
                  :title="$t('ui.captureASignatureRightNowOnThisDevice')"
                  @click="toggleInPersonPanel(doc.id)"
                >
                  <MdiPencil class="w-3.5 h-3.5 shrink-0" />
                  <span class="hidden sm:inline">{{ $t('projectView.signHere') }}</span>
                </button>
              </template>
              <button
                class="btn ghost small flex items-center gap-1"
                :title="$t('documents.previewTitle')"
                @click="openDocPreview(doc)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 shrink-0">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <span class="hidden sm:inline">{{ $t('import.preview') }}</span>
              </button>
              <button
                class="btn ghost small flex items-center gap-1"
                :title="$t('documents.downloadTitle')"
                :disabled="downloadingDocId === doc.id"
                @click="downloadDoc(doc)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 shrink-0">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span class="hidden sm:inline">{{ downloadingDocId === doc.id ? $t('projectView.downloading') : $t('common.download') }}</span>
              </button>
            </div>
          </div>

          <!-- Inline remote sign-request form (email) -->
          <div
            v-if="signFormDocId === doc.id"
            class="mx-3 mb-3 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]"
          >
            <div class="section-label mb-2">{{ $t('projectView.requestSignatureEmail') }}</div>
            <div class="form-row">
              <BaseFormField :label="$t('projectView.signerName')" required>
          <BaseInput v-model="signForm.signer_name" placeholder="Ola Nordmann" />
        </BaseFormField>
              <BaseFormField :label="$t('projectView.signerEmail')" required>
          <BaseInput type="email" v-model="signForm.signer_email" placeholder="ola@nordmann.no" />
        </BaseFormField>
            </div>
            <BaseFormField :label="$t('projectView.expiresAt')">
              <div class="flex flex-wrap gap-1 mb-1.5">
                <button type="button" class="btn ghost small" @click="setExpiryPreset('24h')">{{ $t('projectView.expiryPreset24h') }}</button>
                <button type="button" class="btn ghost small" @click="setExpiryPreset('3d')">{{ $t('projectView.expiryPreset3d') }}</button>
                <button type="button" class="btn ghost small" @click="setExpiryPreset('7d')">{{ $t('projectView.expiryPreset7d') }}</button>
                <button type="button" class="btn ghost small" @click="setExpiryPreset('eow')">{{ $t('checkout.quickDates.endOfWeek') }}</button>
                <button type="button" class="btn ghost small" @click="setExpiryPreset('2w')">{{ $t('projectView.expiryPreset2w') }}</button>
              </div>
              <BaseInput type="datetime-local" v-model="signForm.expires_at" />
            </BaseFormField>
            <div class="flex gap-2 justify-end mt-2">
              <button class="btn small" @click="signFormDocId = null" type="button">{{ $t('common.cancel') }}</button>
              <button
                class="btn primary small"
                :disabled="creatingSignRequest || !signForm.signer_name || !signForm.signer_email"
                @click="submitSignRequest(doc)"
                type="button"
              >
                {{ creatingSignRequest ? $t('passwordReset.sending') : $t('projectView.sendRequest') }}
              </button>
            </div>
          </div>

          <!-- Inline in-person signing panel -->
          <div
            v-if="inPersonDocId === doc.id"
            class="mx-3 mb-3 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]"
          >
            <div class="section-label mb-2">{{ $t('projectView.signInPerson') }}</div>
            <div class="form-row mb-3">
              <BaseFormField :label="$t('projectView.signerName')" required>
          <BaseInput v-model="inPersonForm.signer_name" placeholder="Ola Nordmann" />
        </BaseFormField>
              <BaseFormField :label="$t('projectView.sendSignedPdfTo')">
          <BaseInput type="email" v-model="inPersonForm.send_to_email" placeholder="ola@nordmann.no" />
        </BaseFormField>
            </div>
            <!-- Signature canvas -->
            <BaseFormField :label="$t('projectView.signatureLabel')" required>
          <SignatureCanvas
                :ref="setInPersonCanvas"
                class="rounded-lg overflow-hidden border border-[var(--color-border)]"
                style="height: 160px;"
              />
        </BaseFormField>
            <div class="flex gap-2 justify-end mt-3">
              <button class="btn small" @click="inPersonDocId = null" type="button">{{ $t('common.cancel') }}</button>
              <button
                class="btn small"
                type="button"
                @click="inPersonCanvasRef?.clear()"
              >{{ $t('common.clear') }}</button>
              <button
                class="btn primary small"
                :disabled="submittingInPerson || !inPersonForm.signer_name"
                @click="submitInPersonSign"
                type="button"
              >
                {{ submittingInPerson ? $t('projectView.signing') : $t('projectView.signContract') }}
              </button>
            </div>
          </div>

          <!-- Existing sign requests for this document -->
          <template v-if="signRequestsByDoc[doc.id]?.length">
            <div class="mx-3 mb-3">
              <div class="section-label mb-1">{{ $t('projectView.signatureRequests') }}</div>
              <div class="flex flex-col gap-1.5">
                <div
                  v-for="sr in signRequestsByDoc[doc.id]"
                  :key="sr.id"
                  class="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-surface-2)] border border-[var(--color-border)]"
                >
                  <div class="flex-1 min-w-0">
                    <div class="text-[.85rem] text-[var(--color-text)] font-medium truncate">{{ sr.signer_name }}</div>
                    <div class="text-[.75rem] text-[var(--color-muted)] truncate">{{ sr.signer_email || '—' }}</div>
                  </div>
                  <span
                    class="badge shrink-0"
                    :class="{
                      'badge-success': sr.status === 'signed',
                      'badge-warning': sr.status === 'pending',
                      'badge-danger':  sr.status === 'cancelled' || sr.status === 'expired',
                    }"
                  >{{ signingStatusLabel(sr.status, sr.status_display) }}</span>
                  <a
                    v-if="sr.status === 'pending' && sr.token"
                    :href="`/sign/${sr.token}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn-icon shrink-0"
                    :title="$t('ui.openSigningPage')"
                  >
                    <MdiOpenInNew class="w-3.5 h-3.5" />
                  </a>
                  <button
                    v-if="sr.status === 'pending'"
                    class="btn-icon danger shrink-0"
                    :title="$t('ui.cancelRequest')"
                    :disabled="cancellingSignRequestId === sr.id"
                    @click="onCancelSignRequest(sr)"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </template>
          </div><!-- end per-doc wrapper -->
        </div><!-- end list-stack -->
        <div v-else class="state-empty mb-3">{{ $t('projectView.noDocumentsYet') }}</div>
      </div>
      <!-- ── PDF Previews (existing projects only — temporary) ── -->
      <div v-if="!isNew" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="section-label">{{ $t('projectView.previewDocuments') }}</div>
        <div class="flex flex-wrap gap-2 mt-2">
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-gray-300 text-[.8rem] cursor-pointer hover:bg-gray-700 disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
            @click="openPdfPreview('offer')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            {{ $t('documents.docTypeOffer') }}
          </button>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-gray-300 text-[.8rem] cursor-pointer hover:bg-gray-700 disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
            @click="openPdfPreview('contract')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            {{ $t('documents.docTypeContract') }}
          </button>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-gray-300 text-[.8rem] cursor-pointer hover:bg-gray-700 disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
            @click="openPdfPreview('picking-list')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            {{ $t('documents.docTypePickingList') }}
          </button>
        </div>
      </div>

      <!-- ── Danger zone (existing projects only) ── -->
      <div v-if="!isNew && showFullEditor" class="px-4 py-5">
        <div class="section-label danger">{{ $t('common.dangerZone') }}</div>
        <button class="btn danger w-full" :disabled="deleting" @click="onDelete">
          {{ deleting ? $t('common.deleting') : $t('projectView.deleteProject') }}
        </button>
      </div>

    </template>

    <BaseConfirmModal
      :show="showDeleteConfirm"
      :title="$t('projectView.deleteConfirmTitle')"
      :body="$t('projectView.deleteConfirmBody', { name: form.name })"
      :confirm-label="$t('common.delete')"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="showDeleteConfirm = false"
    />

    <!-- ── PDF Preview Modal ── -->
    <PdfPreviewModal
      :show="pdfModalOpen"
      :url="pdfUrl"
      :title="pdfTitle"
      :download-name="pdfDownloadName"
      @close="closePdfModal"
    />

  </div>
</template>

<script setup>
import { useStatusLabels } from '@/composables/useStatusLabels.js'
const { signingStatusLabel } = useStatusLabels()

import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import { useAuthStore } from '@/stores/auth.js'
import { useShellModeStore } from '@/stores/shellMode.js'
import ProjectActions from '@/components/ProjectActions.vue'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import PdfPreviewModal from '@/components/base/PdfPreviewModal.vue'
import RunMacroButton from '@/components/macros/RunMacroButton.vue'
import SignatureCanvas from '@/components/base/SignatureCanvas.vue'
import MdiOpenInNew from '~icons/mdi/open-in-new'
import MdiPencil from '~icons/mdi/pencil'
import {
  getProject, createProject, updateProject, deleteProject as deleteProjectApi,
  searchCustomersApi, getCustomers, postCustomer, generateInvoice, getInvoice, updateInvoice, getProjectInvoices,
  getActiveCheckouts, markCheckoutReturned,
  generateContract, generateOffer, generatePickingList, getDocuments, localSignContract,
  getSignRequests, createSignRequest, cancelSignRequest, deleteSignRequest,
} from '@/api/api.js'

const router = useRouter()
const route  = useRoute()
const toast  = useToast()
const { t }  = useI18n()
const shell = useShellModeStore()

const projectId = computed(() => route.params.projectId)
const isNew     = computed(() => !projectId.value || projectId.value === 'new')
const isWarehouseGlance = computed(() => shell.isWarehouse && !isNew.value && route.query.edit !== 'full')
const showFullEditor = computed(() => !isWarehouseGlance.value)

// ─── form state ────────────────────────────────────────────────────────────────
const form = ref({
  name:                '',
  location:            '',
  description:         '',
  start_time_local:    '',
  end_time_local:      '',
  usage_days:          '',
  discount_percentage: '',
  status:              0,
  customer:            null,   // integer ID or null
})

// Read-only pricing returned by the API after load/save
const projectStats = ref({ total_rental_price: null, total_rental_price_after_discount: null })

// Fallback: compute usage days locally (used for new projects before first save)
const calculatedUsageDays = computed(() => {
  const start = form.value.start_time_local
  const end   = form.value.end_time_local
  if (!start || !end) return null
  const diffMs = new Date(end) - new Date(start)
  if (diffMs <= 0) return null
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
})

function formatPrice(val) {
  if (val === null || val === undefined) return null
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val)
}

const formValid = computed(() =>
  form.value.name.trim() &&
  form.value.location.trim() &&
  form.value.start_time_local &&
  form.value.end_time_local
)

// ─── load state ────────────────────────────────────────────────────────────────
const loading   = ref(false)
const loadError = ref(null)
const saving    = ref(false)
const deleting  = ref(false)
const showDeleteConfirm = ref(false)

// ─── invoice state ─────────────────────────────────────────────────────────────
const invoice          = ref(null)   // ProjectInvoice from last generate or load
const generatingInvoice = ref(false)
const exportingInvoice  = ref(false)
const markingInvoiced   = ref(false)
const invoiceError      = ref(null)  // inline error from last generate attempt

// ─── customer picker ───────────────────────────────────────────────────────────
const customerQuery    = ref('')
const customerResults  = ref([])
const customerSearching = ref(false)
const customerDropOpen  = ref(false)
const selectedCustomer  = ref(null)  // { id, name }
let   customerTimer     = null
const showNewCustomerForm = ref(false)
const newCustomerForm = ref({ name: '', email: '', phone: '', address: '', contact_person: '' })
const savingCustomer = ref(false)

function onCustomerInput() {
  selectedCustomer.value = null
  form.value.customer    = null
  clearTimeout(customerTimer)
  if (customerQuery.value.length < 2) {
    customerResults.value = []
    return
  }
  customerSearching.value = true
  customerTimer = setTimeout(async () => {
    try {
      const res = await searchCustomersApi(customerQuery.value)
      customerResults.value = res.results ?? res ?? []
    } catch {
      customerResults.value = []
    } finally {
      customerSearching.value = false
    }
  }, 300)
}

function selectCustomer(c) {
  selectedCustomer.value = c
  form.value.customer    = c.id
  customerQuery.value    = c.name
  customerDropOpen.value = false
  customerResults.value  = []
}

function clearCustomer() {
  selectedCustomer.value = null
  form.value.customer    = null
  customerQuery.value    = ''
  customerResults.value  = []
}

function onCustomerBlur() {
  setTimeout(() => { customerDropOpen.value = false }, 150)
}

function toggleNewCustomerForm() {
  showNewCustomerForm.value = !showNewCustomerForm.value
  if (showNewCustomerForm.value) {
    newCustomerForm.value = { name: '', email: '', phone: '', address: '', contact_person: '' }
  }
}

async function saveNewCustomer() {
  if (!newCustomerForm.value.name.trim()) {
    toast.error(t('projectView.customerNameRequired'))
    return
  }
  
  savingCustomer.value = true
  try {
    const response = await postCustomer(newCustomerForm.value)
    // Auto-select the newly created customer
    selectedCustomer.value = response
    form.value.customer = response.id
    customerQuery.value = response.name
    toast.success(t('projectView.customerCreated'))
    showNewCustomerForm.value = false
    newCustomerForm.value = { name: '', email: '', phone: '', address: '', contact_person: '' }
  } catch (error) {
    toast.error(t('projectView.customerCreateFailed'))
    console.error(error)
  } finally {
    savingCustomer.value = false
  }
}

// ─── datetime helpers ──────────────────────────────────────────────────────────
function toLocalInput(iso) {
  if (!iso) return ''
  // "2025-03-15T18:00:00Z" → "2025-03-15T18:00"
  return iso.slice(0, 16)
}

function fromLocalInput(local) {
  if (!local) return null
  return new Date(local).toISOString()
}

// ─── load project ──────────────────────────────────────────────────────────────
async function load() {
  if (isNew.value) {
    // Pre-fill customer if navigated from customer detail view
    const customerIdParam = route.query.customer
    if (customerIdParam) {
      try {
        const res = await getCustomers(Number(customerIdParam))
        if (res?.id) {
          selectedCustomer.value = { id: res.id, name: res.name }
          customerQuery.value    = res.name
          form.value.customer    = res.id
        }
      } catch { /* ignore */ }
    }
    return
  }
  loading.value   = true
  loadError.value = null
  try {
    const project = await getProject(projectId.value)
    form.value.name             = project.name       ?? ''
    form.value.location         = project.location   ?? ''
    form.value.description      = project.description ?? ''
    form.value.start_time_local = toLocalInput(project.start_time)
    form.value.end_time_local   = toLocalInput(project.end_time)
    form.value.usage_days          = project.usage_days ?? ''
    form.value.discount_percentage = project.discount_percentage ?? ''
    form.value.status              = project.status ?? 0
    projectStats.value = {
      total_rental_price:               project.total_rental_price ?? null,
      total_rental_price_after_discount: project.total_rental_price_after_discount ?? null,
    }

    // Populate customer display name if a customer is linked
    const customerId = typeof project.customer === 'object' && project.customer !== null
      ? project.customer.id
      : project.customer
    form.value.customer = customerId ?? null
    if (customerId) {
      try {
        const res = await getCustomers(customerId)
        if (res?.id) {
          selectedCustomer.value = { id: res.id, name: res.name }
          customerQuery.value    = res.name
        }
      } catch { /* ignore — customer name will just be blank */ }
    }

    // Load most recent invoice for this project
    try {
      const res = await getProjectInvoices(projectId.value)
      const invoices = res.results ?? res ?? []
      if (invoices.length) invoice.value = invoices[0]
    } catch { /* non-fatal */ }
  } catch (e) {
    loadError.value = t('projectView.loadFailed')
  } finally {
    loading.value = false
  }
}

// ─── invoice ───────────────────────────────────────────────────────────────────
async function onGenerateInvoice() {
  if (generatingInvoice.value) return
  generatingInvoice.value = true
  invoiceError.value = null
  try {
    const result = await generateInvoice(projectId.value)
    invoice.value = result
    toast.success(t('projectView.invoiceGenerated'))
  } catch (e) {
    const detail = e?.data?.error ?? e?.data?.detail ?? e?.message ?? t('projectView.invoiceGenerateFailed')
    invoiceError.value = detail
  } finally {
    generatingInvoice.value = false
  }
}

async function onMarkInvoiced() {
  if (!invoice.value || markingInvoiced.value) return
  markingInvoiced.value = true
  try {
    const result = await updateInvoice(invoice.value.id, { status: 'invoiced' })
    invoice.value = result
    toast.success(t('projectView.invoiceMarkedInvoiced'))
  } catch (e) {
    toast.error(e?.data?.detail ?? t('projectView.invoiceUpdateFailed'))
  } finally {
    markingInvoiced.value = false
  }
}

async function exportInvoice() {
  exportingInvoice.value = true
  try {
    const { baseURL } = await import('@/api/apiClient.js')
    const authStore = useAuthStore()
    const headers = new Headers({ Accept: 'text/csv' })
    if (authStore.accessToken) headers.append('Authorization', `Bearer ${authStore.accessToken}`)
    if (authStore.currentTenant?.slug) headers.append('X-Tenant-Slug', authStore.currentTenant.slug)

    const res = await fetch(`${baseURL}invoice/${invoice.value.id}/export/`, { headers })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    const csv      = await res.text()
    const blob     = new Blob([csv], { type: 'text/csv' })
    const url      = URL.createObjectURL(blob)
    const filename = `invoice-${invoice.value.id}.csv`
    const a        = document.createElement('a')
    a.href         = url
    a.download     = filename
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  } catch (e) {
    toast.error(t('projectView.exportFailed'))
  } finally {
    exportingInvoice.value = false
  }
}

// ─── save ──────────────────────────────────────────────────────────────────────
async function save() {
  if (!formValid.value || saving.value) return
  saving.value = true
  try {
    const payload = {
      name:        form.value.name.trim(),
      location:    form.value.location.trim(),
      description: form.value.description,
      start_time:  fromLocalInput(form.value.start_time_local),
      end_time:    fromLocalInput(form.value.end_time_local),
      status:      form.value.status,
      customer:    form.value.customer ? { type: 'Customer', id: form.value.customer } : null,
    }
    payload.usage_days          = (form.value.usage_days !== '' && form.value.usage_days !== null) ? form.value.usage_days : null
    payload.discount_percentage = (form.value.discount_percentage !== '' && form.value.discount_percentage !== null) ? form.value.discount_percentage : null

    if (isNew.value) {
      const project = await createProject(payload)
      toast.success(t('projectView.created'))
      router.replace(`/project/${project.id}`)
    } else {
      const project = await updateProject(projectId.value, payload)
      projectStats.value = {
        total_rental_price:               project?.total_rental_price ?? projectStats.value.total_rental_price,
        total_rental_price_after_discount: project?.total_rental_price_after_discount ?? projectStats.value.total_rental_price_after_discount,
      }
      toast.success(t('projectView.saved'))
    }
  } catch (e) {
    const detail = e?.data?.detail ?? e?.data?.name?.[0] ?? e?.message ?? t('projectView.saveFailed')
    toast.error(detail)
  } finally {
    saving.value = false
  }
}

// ─── status advance ────────────────────────────────────────────────────────────
const advancingStatus = ref(false)

const STATUS_NEXT = computed(() => ({
  0: [{ status: 1, label: t('projectView.statusActionConfirm') }],
  1: [{ status: 2, label: t('projectView.statusActionMarkReady') }],
  2: [{ status: 3, label: t('projectView.statusActionCustomerPicksUp') }, { status: 4, label: t('projectView.statusActionDeliver') }],
  3: [{ status: 5, label: t('projectView.statusActionSetActive') }],
  4: [{ status: 5, label: t('projectView.statusActionSetActive') }],
  5: [{ status: 6, label: t('projectView.statusActionMarkReturning') }],
  6: [{ status: 7, label: t('projectView.statusActionComplete') }],
}))

const statusActions = computed(() => ({
  next:   STATUS_NEXT.value[form.value.status] ?? [],
  cancel: form.value.status < 7,
}))

async function advanceStatus(newStatus) {
  if (advancingStatus.value) return
  advancingStatus.value = true
  try {
    await updateProject(projectId.value, { status: newStatus })
    form.value.status = newStatus
    toast.success(t('projectView.statusUpdated'))
  } catch (e) {
    const detail = e?.data?.detail ?? e?.message ?? t('projectView.statusUpdateFailed')
    toast.error(detail)
  } finally {
    advancingStatus.value = false
  }
}

// ─── delete ────────────────────────────────────────────────────────────────────
function onDelete() {
  showDeleteConfirm.value = true
}

async function confirmDelete() {
  deleting.value = true
  try {
    await deleteProjectApi(projectId.value)
    toast.success(t('projectView.deleted'))
    router.replace('/projects')
  } catch {
    toast.error(t('projectView.deleteFailed'))
    deleting.value = false
    showDeleteConfirm.value = false
  }
}

// ─── documents ────────────────────────────────────────────────────────────────
const projectDocuments  = ref([])
const loadingDocuments  = ref(false)
const generatingPdf     = ref(null)     // 'offer' | 'contract' | 'picking-list' | null
const downloadingDocId  = ref(null)     // doc.id currently downloading

function formatDocDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

async function loadDocuments() {
  if (isNew.value) return
  loadingDocuments.value = true
  try {
    const res = await getDocuments({ project: projectId.value, page_size: 100 })
    projectDocuments.value = (res.results ?? res ?? []).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )
  } catch {
    projectDocuments.value = []
  } finally {
    loadingDocuments.value = false
  }
}

async function downloadDoc(doc) {
  if (downloadingDocId.value) return
  downloadingDocId.value = doc.id
  try {
    const { baseURL } = await import('@/api/apiClient.js')
    const authStore = useAuthStore()
    const headers = new Headers()
    if (authStore.accessToken) headers.append('Authorization', `Bearer ${authStore.accessToken}`)
    if (authStore.currentTenant?.slug) headers.append('X-Tenant-Slug', authStore.currentTenant.slug)

    const res = await fetch(`${baseURL}documents/${doc.id}/download/`, { headers })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    const blob     = await res.blob()
    const url      = URL.createObjectURL(blob)
    const title    = (doc.title || doc.document_type_display || `document-${doc.id}`)
      .replace(/[^a-z0-9_\-. ]/gi, '_')
    const a        = document.createElement('a')
    a.href         = url
    a.download     = `${title}.pdf`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  } catch {
    toast.error(t('projectView.downloadFailed'))
  } finally {
    downloadingDocId.value = null
  }
}

async function onGeneratePdf(type) {
  if (generatingPdf.value) return
  generatingPdf.value = type
  try {
    const generators = {
      'offer':        () => generateOffer(projectId.value),
      'contract':     () => generateContract(projectId.value),
      'picking-list': () => generatePickingList(projectId.value),
    }
    await generators[type]()
    toast.success(t('projectView.documentGenerated'))
    await loadDocuments()
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('projectView.generateDocFailed'))
  } finally {
    generatingPdf.value = null
  }
}

// ─── sign requests ────────────────────────────────────────────────────────────
const signRequests            = ref([])   // all sign requests for this project
const signFormDocId           = ref(null) // doc.id whose inline form is open
const signForm                = ref({ signer_name: '', signer_email: '', expires_at: '' })
const creatingSignRequest     = ref(false)
const cancellingSignRequestId = ref(null)

const signRequestsByDoc = computed(() => {
  const map = {}
  for (const sr of signRequests.value) {
    const docId = typeof sr.document === 'object' ? sr.document?.id : sr.document
    if (docId != null) {
      if (!map[docId]) map[docId] = []
      map[docId].push(sr)
    }
  }
  return map
})

async function loadSignRequests() {
  if (isNew.value) return
  try {
    const res = await getSignRequests({ project: projectId.value, page_size: 100 })
    signRequests.value = res.results ?? res ?? []
  } catch {
    signRequests.value = []
  }
}

function toLocalDateTimeInput(date) {
  const pad = n => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function defaultExpiry() {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return toLocalDateTimeInput(d)
}

function setExpiryPreset(preset) {
  const now = new Date()
  if (preset === '24h') {
    now.setHours(now.getHours() + 24)
    signForm.value.expires_at = toLocalDateTimeInput(now)
  } else if (preset === '3d') {
    now.setDate(now.getDate() + 3)
    signForm.value.expires_at = toLocalDateTimeInput(now)
  } else if (preset === '7d') {
    now.setDate(now.getDate() + 7)
    signForm.value.expires_at = toLocalDateTimeInput(now)
  } else if (preset === 'eow') {
    // End of Friday this week (or next Friday if today is Sat/Sun)
    const dow = now.getDay() // 0=Sun,6=Sat
    const daysUntilFriday = dow <= 5 ? (5 - dow) || 7 : 6
    now.setDate(now.getDate() + daysUntilFriday)
    now.setHours(23, 59, 0, 0)
    signForm.value.expires_at = toLocalDateTimeInput(now)
  } else if (preset === '2w') {
    now.setDate(now.getDate() + 14)
    signForm.value.expires_at = toLocalDateTimeInput(now)
  }
}

function toggleSignForm(docId) {
  if (signFormDocId.value === docId) {
    signFormDocId.value = null
  } else {
    signForm.value = { signer_name: '', signer_email: '', expires_at: defaultExpiry() }
    signFormDocId.value = docId
    inPersonDocId.value = null // close in-person panel if open
  }
}

async function submitSignRequest(doc) {
  if (creatingSignRequest.value) return
  creatingSignRequest.value = true
  try {
    const payload = {
      project_id: parseInt(projectId.value, 10),
      document_id: doc.id,
      signer_name: signForm.value.signer_name.trim(),
      signer_email: signForm.value.signer_email.trim(),
    }
    if (signForm.value.expires_at) {
      payload.expires_at = new Date(signForm.value.expires_at).toISOString()
    }
    const result = await createSignRequest(payload)
    signRequests.value = [result, ...signRequests.value]
    signFormDocId.value = null
    toast.success(t('projectView.signRequestSent'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('projectView.failedSignRequest'))
  } finally {
    creatingSignRequest.value = false
  }
}

async function onCancelSignRequest(sr) {
  if (cancellingSignRequestId.value) return
  cancellingSignRequestId.value = sr.id
  try {
    await cancelSignRequest(sr.id)
    // Refresh sign requests to get updated status
    await loadSignRequests()
    toast.success(t('projectView.signRequestCancelled'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('projectView.failedCancelSignRequest'))
  } finally {
    cancellingSignRequestId.value = null
  }
}

// ─── in-person signing ────────────────────────────────────────────────────────
const inPersonDocId       = ref(null) // doc.id whose in-person panel is open
const inPersonForm        = ref({ signer_name: '', send_to_email: '' })
const inPersonCanvasRef   = ref(null)
const submittingInPerson  = ref(false)

function setInPersonCanvas(canvas) {
  inPersonCanvasRef.value = canvas
}

function toggleInPersonPanel(docId) {
  if (inPersonDocId.value === docId) {
    inPersonDocId.value = null
  } else {
    inPersonForm.value = { signer_name: '', send_to_email: '' }
    inPersonDocId.value = docId
    signFormDocId.value = null // close email form if open
  }
}

async function submitInPersonSign() {
  if (submittingInPerson.value) return
  const canvas = inPersonCanvasRef.value
  if (!canvas || canvas.isEmpty) {
    toast.error(t('projectView.pleaseDraw'))
    return
  }
  submittingInPerson.value = true
  try {
    const payload = {
      document_id: inPersonDocId.value,
      signer_name: inPersonForm.value.signer_name.trim(),
      signature_data: canvas.getImageData(),
    }
    const email = inPersonForm.value.send_to_email.trim()
    if (email) payload.send_to_email = email
    await localSignContract(projectId.value, payload)
    inPersonDocId.value = null
    toast.success(t('projectView.contractSigned'))
    await loadDocuments()
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('projectView.failedSignContract'))
  } finally {
    submittingInPerson.value = false
  }
}

// ─── check in all ─────────────────────────────────────────────────────────────
const checkingInAll = ref(false)

async function checkInAll() {
  if (checkingInAll.value) return
  checkingInAll.value = true
  try {
    const res = await getActiveCheckouts({ project: projectId.value, page_size: 200 })
    const active = res.results ?? res ?? []
    if (!active.length) {
      toast.success(t('projectView.noActiveCheckouts'))
      return
    }
    const results = await Promise.allSettled(
      active.map(co => markCheckoutReturned(co.id))
    )
    const failed = results.filter(r => r.status === 'rejected').length
    if (failed) {
      toast.warning(t('projectView.checkInPartial', { succeeded: active.length - failed, failed }))
    } else {
      toast.success(t('projectView.checkedIn', { count: active.length }))
    }
  } catch {
    toast.error(t('projectView.failedCheckIn'))
  } finally {
    checkingInAll.value = false
  }
}

// ─── pdf preview ──────────────────────────────────────────────────────────────
const pdfModalOpen    = ref(false)
const pdfUrl          = ref(null)
const pdfTitle        = ref('')
const pdfDownloadName = ref('document.pdf')

const PDF_LABELS = computed(() => ({
  offer: t('projectView.offerLabel'),
  contract: t('projectView.contractLabel'),
  'picking-list': t('projectView.pickingListLabel'),
}))

async function openPdfPreview(type) {
  const { baseURL } = await import('@/api/apiClient.js')
  pdfTitle.value        = PDF_LABELS.value[type] ?? type
  pdfDownloadName.value = `${PDF_LABELS.value[type] ?? type}.pdf`
  pdfUrl.value          = `${baseURL}pdf/preview/${type}/${projectId.value}/`
  pdfModalOpen.value    = true
}

function openDocPreview(doc) {
  pdfTitle.value        = doc.title || doc.document_type_display
  pdfDownloadName.value = `${doc.title || doc.document_type_display}.pdf`
  pdfUrl.value          = doc.download_url
  pdfModalOpen.value    = true
}

function closePdfModal() {
  pdfModalOpen.value = false
  pdfUrl.value       = null
}

// ─── lifecycle ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  await load()
  await Promise.all([loadDocuments(), loadSignRequests()])
})

// Re-run load when navigating new → existing (after create redirects via router.replace)
watch(projectId, (newId, oldId) => {
  if (newId && newId !== oldId && newId !== 'new') load()
})
</script>
